/**
 * ETL Integrator API client — currently just `POST /generate` (E3's
 * handler.py). Mirrors tengrade's web-examiner/src/api/client.ts shape
 * (ACTION_PLAN.md E4): same `request()`/`ApiError` pattern, every call
 * carries the Cognito ID token.
 */

import { currentIdToken } from '../auth/cognito';
import { config } from '../config';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** A failed §8 layer-3 content-safety check (generate/handler.py's
   *  422 response) carries a human-readable reason. */
  get reason(): string | undefined {
    return typeof this.body.reason === 'string' ? this.body.reason : undefined;
  }
}

// The API Gateway HTTP API this client talks to has a fixed 30s
// integration timeout it cannot exceed (apigwv2.HttpApi's own ceiling,
// regardless of any individual Lambda's own configured timeout) — a
// slow backend call always resolves as a real HTTP response (504) well
// under this, never an unbounded hang. This timeout exists for the
// other failure mode: the request never reaching a server at all (a
// dropped connection, a stalled proxy) — AbortSignal.timeout turns
// that into a real rejection instead of a Promise that never settles,
// which is what every call site's `finally { setBusy(false) }` needs
// to actually run.
const REQUEST_TIMEOUT_MS = 45_000;

async function request<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const token = await currentIdToken();
  if (!token) throw new ApiError(401, 'Your session has expired. Please sign in again.');

  let response: Response;
  try {
    response = await fetch(`${config.apiUrl}${path}`, {
      method: init.method ?? 'GET',
      headers: {
        authorization: `Bearer ${token}`,
        ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch (err) {
    const timedOut = err instanceof DOMException && err.name === 'TimeoutError';
    throw new ApiError(
      0,
      timedOut
        ? 'The request timed out. Please try again.'
        : 'Could not reach the ETL Integrator API. Check your connection.',
    );
  }

  const text = await response.text();
  let body: Record<string, unknown> = {};
  if (text) {
    try {
      body = JSON.parse(text) as Record<string, unknown>;
    } catch {
      body = { error: text };
    }
  }

  if (!response.ok) {
    const message =
      typeof body.error === 'string' ? body.error : `Request failed (${response.status}).`;
    throw new ApiError(response.status, message, body);
  }
  return body as T;
}

// ── generate (E3/E5 generate/handler.py, schemas/generation.py) ─────

/** Matches schemas/canonical.py's `SchemaField`/`CanonicalSchema` shape
 *  exactly — the wire shape of a parsed/inferred schema, round-tripped
 *  through the E5 §3 review/confirm flow below. */
export interface CanonicalField {
  name: string;
  type: string;
  nullable: boolean;
  constraints?: Record<string, unknown>;
  children?: CanonicalField[] | null;
}

export interface CanonicalSchemaShape {
  name: string;
  source_format: string;
  fields: CanonicalField[];
  raw_metadata?: Record<string, unknown>;
}

export interface SchemaInput {
  format: string;
  content: string;
  /** E5 §3 — when set, the handler uses this directly instead of
   *  parsing/inferring `content` again: the second call of the
   *  review/confirm round trip. */
  confirmed_schema?: CanonicalSchemaShape;
}

/** Matches schemas/generation.py's `GenerationOptions` exactly. */
export interface GenerationOptions {
  python_version?: string;
  field_mapping_overrides?: Record<string, string>;
  review_inferred_schema?: boolean;
  /** E9 — asks generate/handler.py to also generate a pytest suite
   *  for the script. Bundled into the same job's price, no free-tier
   *  restriction. */
  include_tests?: boolean;
}

/** lib/complexity.py's ComplexityTier — the single pre-call complexity
 *  signal reused for model routing, predictive pricing, and free-tier
 *  admission gating alike. */
export type ComplexityTier = 'low' | 'medium' | 'high' | 'extra';

/** §7.1 — the pricing decision one /generate call actually ran under,
 *  carried on generate/handler.py's 200 response so a completed job's
 *  cost never has to be looked up separately. `price_expected_gbp_pence`
 *  and `price_charged_gbp_pence` are equal for every paid job today (the
 *  hard pre-check-and-refuse model has no metered adjustment after the
 *  fact) — kept as two fields rather than one for a free job, where
 *  charged is always 0 but expected still shows what the same job would
 *  have cost on the paid tier. */
export interface JobPricing {
  plan: 'free' | 'paid';
  tier: ComplexityTier;
  model: string;
  price_expected_gbp_pence: number;
  price_charged_gbp_pence: number;
}

/** Matches `@fibtecltd/iron-triangle`'s `Triangle` shape exactly (the
 *  Python package's own `triangle()` return value, JSON-serialised) —
 *  generate/handler.py's inline `"triangle"` field. */
export interface JobTriangle {
  c: number;
  t: number;
  q: number;
  a_hat: number;
  tes: number;
  flags: ('cost' | 'time' | 'quality')[];
  it_version: string;
}

/** E16a — a static check of what was generated (api/lib/generated_check.py): it reads the script
 *  and the tests and runs neither, so it never says the tests pass. `tests` is null when none
 *  were requested. */
export interface GenerationValidation {
  script: 'ok' | 'syntax_error' | 'not_checked';
  tests: {
    static: 'ok' | 'rewritten' | 'names_missing' | 'not_checked' | 'syntax_error';
  } | null;
}

/** Matches schemas/generation.py's `GeneratedCode` response fields —
 *  `token_cost`/`latency_ms` are billing/ops metadata (§8), not surfaced
 *  by generate/handler.py's 200 response body, so they're not typed here. */
export interface GeneratedCode {
  script: string;
  /** E9 — the generated pytest suite, present only when
   *  GenerationOptions.include_tests was set on the request. */
  tests: string | null;
  /** Optional: an API deployed before E16a does not send it. */
  validation?: GenerationValidation;
  unresolved_fields: string[];
  pricing: JobPricing;
  /** Iron Triangle (ACTION_PLAN.md §7/§9) — the one real, fully-populated
   *  triangle for this job, computed from the same metadata the job's
   *  own audit row was written with. */
  triangle: JobTriangle;
}

/** E5 §3 — the review-only response shape, returned instead of
 *  GeneratedCode when GenerationOptions.review_inferred_schema is true. */
export interface SchemaReviewResult {
  review: {
    source: CanonicalSchemaShape;
    destination: CanonicalSchemaShape;
  };
}

export type GenerateResult = GeneratedCode | SchemaReviewResult;

export const generateEtl = (
  source: SchemaInput,
  destination: SchemaInput,
  options?: GenerationOptions,
) =>
  request<GenerateResult>('/generate', {
    method: 'POST',
    body: { source, destination, ...(options ? { options } : {}) },
  });

/** generate_estimate/handler.py's pre-generation cost quote — Iron
 *  Triangle's cost axis, known before Generate is clicked. Deliberately
 *  not a triangle shape: time and quality don't exist until a job
 *  actually runs, and fabricating zeros for them would always score a
 *  perfect TES regardless of the real cost (the degenerate two-zero-axis
 *  case `fibtec-iron-triangle`'s own spec names as a documented weakness
 *  — see generate_estimate/handler.py's module docstring). */
export interface GenerateEstimate {
  tier: ComplexityTier;
  model: string;
  price_expected_gbp_pence: number;
  dev_cost_gbp_pence: number;
  dev_hours: number;
}

export const getGenerateEstimate = (source: SchemaInput, destination: SchemaInput) =>
  request<GenerateEstimate>('/generate/estimate', {
    method: 'POST',
    body: { source, destination },
  });

// ── generate/connector (E10a generate_connector/handler.py) ─────────

/** Matches schemas/connector.py's GeneratedFile exactly. */
export interface GeneratedFile {
  path: string;
  content: string;
}

/** Matches schemas/connector.py's GeneratedConnector exactly — the
 *  single-api-surface counterpart to GeneratedCode above, kept as its
 *  own type rather than a variant of it (design spec §3). */
export interface GeneratedConnector {
  files: GeneratedFile[];
  unresolved_operations: string[];
  pricing: JobPricing;
}

/** E10b — "openapi_surface" (default) or "graphql_introspection".
 *  E12 — `platform` is an optional hint (e.g. "stripe") naming the
 *  target SaaS platform; matches schemas/generation.py's
 *  GenerationOptions.platform exactly — unset or unrecognized values
 *  fail open (no few-shot boost), never an error. */
export const generateConnector = (
  document: Record<string, unknown>,
  options?: { format?: string; endpointPath?: string; platform?: string },
) =>
  request<GeneratedConnector>('/generate/connector', {
    method: 'POST',
    body: {
      api_surface: {
        document,
        ...(options?.format ? { format: options.format } : {}),
        ...(options?.endpointPath ? { endpoint_path: options.endpointPath } : {}),
      },
      ...(options?.platform ? { options: { platform: options.platform } } : {}),
    },
  });

// ── generate/webhook (E11 generate_webhook/handler.py) ──────────────

/** Matches schemas/webhook_surface.py's SignatureScheme exactly. */
export interface SignatureScheme {
  kind: 'none' | 'hmac_sha256_hex' | 'hmac_sha256_base64' | 'hmac_sha1_hex';
  header_name?: string;
  signed_payload_template?: string;
  timestamp_header_name?: string;
}

/** Matches schemas/webhook_surface.py's WebhookEvent exactly. */
export interface WebhookEvent {
  event_type: string;
  payload_schema: CanonicalSchemaShape;
  target_schema?: CanonicalSchemaShape;
  idempotency_key_path?: string;
}

/** Matches schemas/webhook_surface.py's WebhookSurface exactly — no
 *  parser produces this one (design spec §1), so the caller builds it
 *  directly rather than uploading a document for the backend to parse. */
export interface WebhookSurface {
  name: string;
  source_format: 'webhook_declaration';
  signature: SignatureScheme;
  event_type_field_path: string;
  events: WebhookEvent[];
  dlq_declared?: boolean;
}

export const generateWebhook = (surface: WebhookSurface) =>
  request<GeneratedConnector>('/generate/webhook', {
    method: 'POST',
    body: { webhook_surface: surface },
  });

// ── billing (E7 billing/handler.py, billing.py) ─────────────────────

export interface Payment {
  amount_pence: number;
  credited_pence: number;
  credited_at: string;
}

/** billing.py's price_table() entry for one complexity tier — the model
 *  a job in that tier actually routes to right now, and what it costs. */
export interface TierPrice {
  model: string;
  price_gbp_pence: number;
}

/** §7.1 — present only when `plan` is `'free'`. */
export interface FreeTierStatus {
  requests_this_month: number;
  monthly_request_cap: number;
  requests_remaining_this_month: number;
}

export interface Billing {
  plan: 'free' | 'paid';
  credit_balance_pence: number;
  payments: Payment[];
  /** Every complexity tier's current (model, price) — always the full
   *  four-tier ladder, even on the free plan (which only ever routes
   *  "low", but the other tiers' prices are what upgrading to paid
   *  would unlock). */
  price_table: Record<ComplexityTier, TierPrice>;
  free_tier?: FreeTierStatus;
}

export const getBilling = () => request<Billing>('/billing');

export const createCheckoutSession = (amountPence: number) =>
  request<{ checkout_url: string }>('/billing/checkout', {
    method: 'POST',
    body: { amount_pence: amountPence },
  });

// ── github install / deliver-pr (E-PR github_install/handler.py,
// deliver_pr/handler.py — docs/fibtec-etlgen-e-pr-design-spec.md §4) ──

export const getGithubInstallUrl = () =>
  request<{ install_url: string }>('/github/install');

/** Lists the repos the caller's org's GitHub App installation already
 *  covers — DeliverAsPr.tsx uses this to offer a dropdown instead of a
 *  free-text "owner/name" field, since the installation already knows
 *  the answer. Throws a 409 ApiError when there's no installation yet
 *  (the same signal `deliverPr` itself returns in that case) — callers
 *  treat that as "not connected", not an error to surface. */
export const getGithubRepos = () => request<{ repos: string[] }>('/github/repos');

/** Lists `repo`'s actual branches — DeliverAsPr.tsx uses this once a
 *  repo is picked to offer a "Base branch" dropdown instead of a
 *  free-text field defaulted to a guessed `"main"` that may not even
 *  exist there. */
export const getGithubBranches = (repo: string) =>
  request<{ branches: string[] }>(`/github/branches?repo=${encodeURIComponent(repo)}`);

/** Step 2 of connecting GitHub: where to send the user so they can authorize the App. The
 *  installation id (from GitHub's setup-URL redirect) is bound into the signed state on the
 *  server, so nothing else needs to carry it. Throws a 403 ApiError when the organisation may
 *  not connect GitHub yet. */
export const getGithubAuthorizeUrl = (installationId: number) =>
  request<{ authorize_url: string }>(
    `/github/authorize?installation_id=${encodeURIComponent(String(installationId))}`,
  );

/** Step 3: the `code` and `state` GitHub redirected back with after the user authorized the
 *  App. The server accepts the installation only if that user can access it. */
export const submitGithubInstallCallback = (code: string, state: string) =>
  request<{ connected: boolean }>('/github/install-callback', {
    method: 'POST',
    body: { code, state },
  });

export const deliverPr = (args: {
  files: GeneratedFile[];
  repo: string;
  baseBranch: string;
  title: string;
  body?: string;
}) =>
  request<{ pr_url: string }>('/generate/deliver-pr', {
    method: 'POST',
    body: {
      files: args.files,
      repo: args.repo,
      base_branch: args.baseBranch,
      title: args.title,
      body: args.body ?? '',
    },
  });

// ── API keys (E15 api_keys/handler.py — docs/fibtec-etlgen-e15-design-spec.md §4) ──

export type ApiKeyScope = 'read' | 'generate';

/** One key as the list route shows it: never the secret, never a hash. */
export interface ApiKey {
  key_id: string;
  label: string;
  scopes: ApiKeyScope[];
  created_at: string;
  created_by: string;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  last_used_at: string | null;
}

/** The create response: `api_key` is the key itself, in full, and exists nowhere else. It is
 *  shown once; the server keeps only a hash. */
export interface CreatedApiKey {
  key_id: string;
  label: string;
  scopes: ApiKeyScope[];
  created_at: string;
  expires_at: string | null;
  api_key: string;
}

/** Owner only: any other caller gets a 403 ApiError. */
export const listApiKeys = () => request<{ api_keys: ApiKey[] }>('/api-keys');

/** `expiresAt` is an ISO 8601 timestamp with a time zone, in the future and at most 366 days
 *  ahead; leave it out for a key that never expires. */
export const createApiKey = (args: {
  label: string;
  scopes: ApiKeyScope[];
  expiresAt?: string;
}) =>
  request<CreatedApiKey>('/api-keys', {
    method: 'POST',
    body: {
      label: args.label,
      scopes: args.scopes,
      ...(args.expiresAt ? { expires_at: args.expiresAt } : {}),
    },
  });

export const revokeApiKey = (keyId: string) =>
  request<{ key_id: string; revoked: boolean; revoked_at: string }>(
    `/api-keys/${encodeURIComponent(keyId)}`,
    { method: 'DELETE' },
  );
